"use client";

import React, { createContext, useContext, useMemo } from "react";
import { useAgentChat } from "../hooks/use-agent-chat";
import type {
  AgentThread,
  AgentMessage,
  AgentSuggestion,
} from "../types/agent.types";
import type { DocumentScopePayload } from "@/components/features/agent/components/agent-document-scope-state";
import type { AgentChatInteraction } from "@/services/agent.service";
import { AgentChatCancellationProvider } from "./AgentChatCancellationContext";

interface AgentChatContextValue {
  isThreadsLoaded: boolean;
  hasThreadsLoadError: boolean;
  hasHistoryLoadError: boolean;
  messages: AgentMessage[];
  isLoading: boolean;
  isCancelling: boolean;
  inputValue: string;
  setInputValue: (value: string) => void;
  sendMessage: (
    content: string,
    reasoningMode?: "fast" | "thinking",
    forcedAction?: "research_context" | "interview_guide" | "documentation" | "project" | null,
    projectIds?: string[],
    documentScope?: DocumentScopePayload,
    interaction?: AgentChatInteraction,
    editOptions?: {
      editOfMessageId: string;
      confirmWorkspaceActions?: boolean;
    }
  ) => Promise<void>;
  editMessage: (
    messageId: string,
    content: string,
    reasoningMode?: "fast" | "thinking",
    forcedAction?: "research_context" | "interview_guide" | "documentation" | "project" | null,
    projectIds?: string[],
    documentScope?: DocumentScopePayload,
    confirmWorkspaceActions?: boolean,
  ) => Promise<void>;
  cancelRequest: () => Promise<void>;
  handleSuggestionClick: (suggestion: AgentSuggestion) => void;
  closeGuidePanel: () => void;
  openGuidePanel: (content: string, title?: string) => void;
  closeResearchContextPanel: () => void;
  openResearchContextPanel: (content: string, title?: string) => void;
  closeReportPanel: () => void;
  openReportPanel: (
    content: string,
    title?: string,
    options?: {
      projectId?: string;
      recordId?: string;
      projectReportId?: string;
      reportType?: import("@/components/features/agent/components/report-panel").RecordReportType;
      anchor?: string;
    }
  ) => void;
  threads: AgentThread[];
  currentThread: AgentThread | null;
  switchThread: (threadId: string) => Promise<void>;
  createNewThread: (name?: string) => Promise<void>;
  renameThread: (threadId: string, newName: string) => Promise<void>;
  deleteThread: (threadId: string) => Promise<void>;
  guideContent: string | null;
  guideTitle: string | null;
  researchContextContent: string | null;
  researchContextTitle: string | null;
  reportPanelContent: string | null;
  reportPanelTitle: string | null;
  reportPanelProjectId: string | null;
  reportPanelRecordId: string | null;
  reportPanelProjectReportId: string | null;
  reportPanelReportType: import("@/components/features/agent/components/report-panel").RecordReportType | null;
  reportPanelAnchor: string | null;
  activePanel: "guide" | "research_context" | "report" | null;
}

const AgentChatContext = createContext<AgentChatContextValue | null>(null);

export function AgentChatProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const agentChatData = useAgentChat();

  const value = useMemo<AgentChatContextValue>(
    () => ({
      isThreadsLoaded: agentChatData.isThreadsLoaded,
      hasThreadsLoadError: agentChatData.hasThreadsLoadError,
      hasHistoryLoadError: agentChatData.hasHistoryLoadError,
      messages: agentChatData.messages,
      isLoading: agentChatData.isLoading,
      isCancelling: agentChatData.isCancelling,
      inputValue: agentChatData.inputValue,
      setInputValue: agentChatData.setInputValue,
      sendMessage: agentChatData.sendMessage,
      editMessage: agentChatData.editMessage,
      cancelRequest: agentChatData.cancelRequest,
      handleSuggestionClick: agentChatData.handleSuggestionClick,
      closeGuidePanel: agentChatData.closeGuidePanel,
      openGuidePanel: agentChatData.openGuidePanel,
      closeResearchContextPanel: agentChatData.closeResearchContextPanel,
      openResearchContextPanel: agentChatData.openResearchContextPanel,
      closeReportPanel: agentChatData.closeReportPanel,
      openReportPanel: agentChatData.openReportPanel,
      threads: agentChatData.threads,
      currentThread: agentChatData.currentThread,
      switchThread: agentChatData.switchThread,
      createNewThread: agentChatData.createNewThread,
      renameThread: agentChatData.renameThread,
      deleteThread: agentChatData.deleteThread,
      guideContent: agentChatData.guideContent,
      guideTitle: agentChatData.guideTitle,
      researchContextContent: agentChatData.researchContextContent,
      researchContextTitle: agentChatData.researchContextTitle,
      reportPanelContent: agentChatData.reportPanelContent,
      reportPanelTitle: agentChatData.reportPanelTitle,
      reportPanelProjectId: agentChatData.reportPanelProjectId,
      reportPanelRecordId: agentChatData.reportPanelRecordId,
      reportPanelProjectReportId: agentChatData.reportPanelProjectReportId,
      reportPanelReportType: agentChatData.reportPanelReportType,
      reportPanelAnchor: agentChatData.reportPanelAnchor,
      activePanel: agentChatData.activePanel,
    }),
    [
      agentChatData.isThreadsLoaded,
      agentChatData.hasThreadsLoadError,
      agentChatData.hasHistoryLoadError,
      agentChatData.messages,
      agentChatData.isLoading,
      agentChatData.isCancelling,
      agentChatData.inputValue,
      agentChatData.setInputValue,
      agentChatData.sendMessage,
      agentChatData.editMessage,
      agentChatData.cancelRequest,
      agentChatData.handleSuggestionClick,
      agentChatData.closeGuidePanel,
      agentChatData.openGuidePanel,
      agentChatData.closeResearchContextPanel,
      agentChatData.openResearchContextPanel,
      agentChatData.closeReportPanel,
      agentChatData.openReportPanel,
      agentChatData.threads,
      agentChatData.currentThread,
      agentChatData.switchThread,
      agentChatData.createNewThread,
      agentChatData.renameThread,
      agentChatData.deleteThread,
      agentChatData.guideContent,
      agentChatData.guideTitle,
      agentChatData.researchContextContent,
      agentChatData.researchContextTitle,
      agentChatData.reportPanelContent,
      agentChatData.reportPanelTitle,
      agentChatData.reportPanelProjectId,
      agentChatData.reportPanelRecordId,
      agentChatData.reportPanelProjectReportId,
      agentChatData.reportPanelReportType,
      agentChatData.reportPanelAnchor,
      agentChatData.activePanel,
    ]
  );

  return (
    <AgentChatCancellationProvider>
      <AgentChatContext.Provider value={value}>
        {children}
      </AgentChatContext.Provider>
    </AgentChatCancellationProvider>
  );
}

export function useAgentChatContext(): AgentChatContextValue | null {
  return useContext(AgentChatContext);
}
